import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { useNavigate } from "react-router-dom";

import { mutate } from "swr";
import { apiClient } from "@/api/client";
import { clearStoredToken, getStoredToken, setStoredToken } from "@/lib/auth";
import { API_BASE_URL } from "@/lib/env";

export interface AuthUser {
  id: string;
  email: string;
  display_name?: string | null;
  language?: string | null;
  notify_enabled?: boolean;
  subscription_status?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRequestingLink: boolean;
  magicLinkSentTo: string | null;
  authError: string | null;
  requestMagicLink: (email: string) => Promise<void>;
  logout: () => void;
  clearAuthError: () => void;
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const [isRequestingLink, setIsRequestingLink] = useState(false);
  const [magicLinkSentTo, setMagicLinkSentTo] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const processedTokensRef = useRef<Set<string>>(new Set());
  const tokenRef = useRef<string | null>(null);
  const initialFetchStartedRef = useRef(false);

  const clearSession = useCallback(
    (message?: string) => {
      clearStoredToken();
      tokenRef.current = null;
      setToken(null);
      setUser(null);
      // Clear all SWR cache entries
      mutate(() => true, undefined, { revalidate: false });
      if (message) {
        setAuthError(message);
      }
    },
    [setAuthError],
  );

  const authFetch = useCallback(
    async (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers ?? {});
      const jwt = tokenRef.current;
      if (jwt) {
        headers.set("Authorization", `Bearer ${jwt}`);
      }

      const response = await fetch(input, { ...init, headers });

      if (response.status === 401) {
        clearSession("Your session expired. Please sign in again.");
      }

      return response;
    },
    [clearSession],
  );

  const refreshUser = useCallback(async () => {
    const jwt = tokenRef.current;
    if (!jwt) return;

    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          clearSession("Your session expired. Please sign in again.");
        }
        throw new Error("Failed to load profile");
      }

      const data = (await response.json()) as AuthUser;
      setUser(data);
    } catch (error) {
      console.error("Failed to refresh user", error);
    }
  }, [clearSession]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    apiClient.setTokenProvider(() => tokenRef.current);
    apiClient.setUnauthorizedHandler(() => {
      clearSession("Your session expired. Please sign in again.");
    });
  }, [clearSession]);

  useEffect(() => {
    // Prevent duplicate fetches from StrictMode double-mounting
    if (initialFetchStartedRef.current) return;

    const savedToken = getStoredToken();

    if (!savedToken) {
      setIsInitializing(false);
      return;
    }

    initialFetchStartedRef.current = true;
    tokenRef.current = savedToken;
    setToken(savedToken);

    refreshUser().finally(() => {
      setIsInitializing(false);
    });
  }, [refreshUser]);

  const stripTokenFromUrl = useCallback(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;

    url.searchParams.delete("token");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const verifyMagicToken = useCallback(
    async (magicToken: string) => {
      setIsVerifyingToken(true);
      setAuthError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/auth/magic/verify`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ token: magicToken }),
        });

        if (!response.ok) {
          throw new Error("The sign-in link is invalid or has expired.");
        }

        const data = (await response.json()) as { token: string; user: AuthUser };
        const jwt = data.token;
        const profile = data.user;

        setStoredToken(jwt);
        tokenRef.current = jwt;
        setToken(jwt);
        setUser(profile);
        setMagicLinkSentTo(null);

        if (profile.subscription_status === "active") {
          navigate("/episodes", { replace: true });
        } else {
          navigate("/paywall", { replace: true });
        }
      } catch (error) {
        console.error("Failed to verify magic link", error);
        const message =
          error instanceof Error ? error.message : "Could not verify sign-in link. Please request a new one.";
        setAuthError(message);
      } finally {
        stripTokenFromUrl();
        setIsVerifyingToken(false);
        setIsInitializing(false);
      }
    },
    [navigate, stripTokenFromUrl],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const magicToken = url.searchParams.get("token");

    if (magicToken && !processedTokensRef.current.has(magicToken)) {
      processedTokensRef.current.add(magicToken);
      void verifyMagicToken(magicToken);
    }
  }, [verifyMagicToken]);

  const requestMagicLink = useCallback(async (email: string) => {
    setIsRequestingLink(true);
    setAuthError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/magic/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Something went wrong while requesting the sign-in link.");
      }

      setMagicLinkSentTo(email);
    } catch (error) {
      console.error("Failed to request magic link", error);
      const message =
        error instanceof Error ? error.message : "We could not send the sign-in link. Please try again.";
      setAuthError(message);
      throw error;
    } finally {
      setIsRequestingLink(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setMagicLinkSentTo(null);
    navigate("/", { replace: true });
  }, [clearSession, navigate]);

  const isAuthenticated = Boolean(user && token);
  const isLoading = isInitializing || isVerifyingToken;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated,
      isLoading,
      isRequestingLink,
      magicLinkSentTo,
      authError,
      requestMagicLink,
      logout,
      clearAuthError: () => setAuthError(null),
      authFetch,
      refreshUser,
    }),
    [
      authError,
      authFetch,
      isAuthenticated,
      isLoading,
      isRequestingLink,
      logout,
      magicLinkSentTo,
      refreshUser,
      requestMagicLink,
      token,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
